import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { FinancialInfoValues } from "../components/customers/prospect-kyc/types";
import { PDF_COLORS, PDF_LAYOUT, PDF_FONTS } from "./pdfConstants";

/**
 * Format currency value
 */
const formatCurrency = (value: string | undefined): string => {
  const num = parseFloat(value || '0');
  if (num === 0) return '$0';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/**
 * Calculate total assets from financial info values
 */
export const calculateTotalAssets = (values: FinancialInfoValues): number => {
  return (
    (parseFloat(values.cashOnHandAndBanks || '') || 0) +
    (parseFloat(values.savingsAccounts || '') || 0) +
    (parseFloat(values.iraOrOtherRetirement || '') || 0) +
    (parseFloat(values.accountsAndNotesReceivable || '') || 0) +
    (parseFloat(values.lifeInsuranceCashSurrender || '') || 0) +
    (parseFloat(values.stocksAndBonds || '') || 0) +
    (parseFloat(values.realEstate || '') || 0) +
    (parseFloat(values.automobileTotalPresentValue || '') || 0) +
    (parseFloat(values.otherPersonalProperty || '') || 0) +
    (parseFloat(values.otherAssets || '') || 0)
  );
};

/**
 * Calculate total liabilities from financial info values
 */
export const calculateTotalLiabilities = (values: FinancialInfoValues): number => {
  return (
    (parseFloat(values.accountsPayable || '') || 0) +
    (parseFloat(values.notesPayableToBanksAndOthers || '') || 0) +
    (parseFloat(values.installmentAccountAuto || '') || 0) +
    (parseFloat(values.installmentAccountOther || '') || 0) +
    (parseFloat(values.loansOnLifeInsurance || '') || 0) +
    (parseFloat(values.mortgagesOnRealEstate || '') || 0) +
    (parseFloat(values.unpaidTaxes || '') || 0) +
    (parseFloat(values.otherLiabilities || '') || 0)
  );
};

/**
 * Create a styled section header
 */
const createSectionHeader = (
  doc: jsPDF,
  title: string,
  yPos: number,
  color: number[]
): number => {
  doc.setFillColor(color[0], color[1], color[2]);
  doc.rect(PDF_LAYOUT.margin, yPos - 5, 182, PDF_LAYOUT.sectionHeaderHeight, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(PDF_FONTS.sectionHeader);
  doc.setFont('helvetica', 'bold');
  doc.text(title, PDF_LAYOUT.margin + 6, yPos + 3);
  return yPos + 15;
};

/**
 * Add page if needed based on y position
 */
const addPageIfNeeded = (doc: jsPDF, yPos: number, threshold: number = 250): { doc: jsPDF; yPos: number } => {
  if (yPos > threshold) {
    doc.addPage();
    return { doc, yPos: 20 };
  }
  return { doc, yPos };
};

/**
 * Generate a beautifully styled PDF document from financial info values
 */
export const generateFinancialInfoPDF = (
  values: FinancialInfoValues,
  totalAssets: number,
  totalLiabilities: number
): void => {
  let doc = new jsPDF();
  let yPos = 20;

  // ==================== HEADER SECTION ====================
  doc.setFillColor(PDF_COLORS.primary[0], PDF_COLORS.primary[1], PDF_COLORS.primary[2]);
  doc.rect(0, 0, PDF_LAYOUT.pageWidth, PDF_LAYOUT.headerHeight, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(PDF_FONTS.title);
  doc.setFont('helvetica', 'bold');
  doc.text('Personal Financial Statement', 105, 20, { align: 'center' });
  
  doc.setFontSize(PDF_FONTS.subtitle);
  doc.setFont('helvetica', 'normal');
  doc.text('Comprehensive Financial Overview Report', 105, 30, { align: 'center' });
  
  doc.setFontSize(PDF_FONTS.date);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`, 105, 37, { align: 'center' });

  yPos = 50;

  // ==================== SUMMARY CARD ====================
  doc.setFillColor(PDF_COLORS.secondaryLight[0], PDF_COLORS.secondaryLight[1], PDF_COLORS.secondaryLight[2]);
  doc.rect(PDF_LAYOUT.margin, yPos, 182, PDF_LAYOUT.summaryCardHeight, 'F');
  
  doc.setDrawColor(PDF_COLORS.primary[0], PDF_COLORS.primary[1], PDF_COLORS.primary[2]);
  doc.setLineWidth(0.5);
  doc.rect(PDF_LAYOUT.margin, yPos, 182, PDF_LAYOUT.summaryCardHeight, 'S');
  
  doc.setTextColor(PDF_COLORS.dark[0], PDF_COLORS.dark[1], PDF_COLORS.dark[2]);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('FINANCIAL SUMMARY', PDF_LAYOUT.margin + 6, yPos + 10);
  
  const col1X = PDF_LAYOUT.margin + 11;
  const col2X = PDF_LAYOUT.margin + 71;
  const col3X = PDF_LAYOUT.margin + 136;
  
  // Total Assets
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
  doc.text('Total Assets', col1X, yPos + 20);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(PDF_COLORS.success[0], PDF_COLORS.success[1], PDF_COLORS.success[2]);
  doc.text(formatCurrency(totalAssets.toString()), col1X, yPos + 28);
  
  // Total Liabilities
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
  doc.text('Total Liabilities', col2X, yPos + 20);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(PDF_COLORS.danger[0], PDF_COLORS.danger[1], PDF_COLORS.danger[2]);
  doc.text(formatCurrency(totalLiabilities.toString()), col2X, yPos + 28);
  
  // Net Worth
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
  doc.text('Net Worth', col3X, yPos + 20);
  const netWorth = totalAssets - totalLiabilities;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const netWorthColor = netWorth >= 0 ? PDF_COLORS.success : PDF_COLORS.danger;
  doc.setTextColor(netWorthColor[0], netWorthColor[1], netWorthColor[2]);
  doc.text(formatCurrency(netWorth.toString()), col3X, yPos + 28);

  yPos = 95;

  // ==================== FINANCIAL OVERVIEW SECTION ====================
  yPos = createSectionHeader(doc, 'Financial Overview', yPos, PDF_COLORS.primary);
  
  const financialOverviewData = [
    ['Personal Income', formatCurrency(values.personalIncome)],
    ['Business Revenue', formatCurrency(values.businessRevenue)],
    ['Source of Funds', values.sourceOfFunds || 'N/A'],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Field', 'Value']],
    body: financialOverviewData,
    theme: 'striped',
    headStyles: {
      fillColor: PDF_COLORS.primary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONTS.tableHeader,
    },
    bodyStyles: {
      fontSize: PDF_FONTS.tableBody,
      textColor: PDF_COLORS.dark,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.secondaryLight,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 'auto' },
    },
    margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
  });
  yPos = (doc as any).lastAutoTable.finalY + 15;

  // ==================== PERSONAL INFORMATION SECTION ====================
  if (values.firstName || values.middleName || values.lastName) {
    const result1 = addPageIfNeeded(doc, yPos);
    doc = result1.doc;
    yPos = result1.yPos;
    
    yPos = createSectionHeader(doc, 'Personal Information', yPos, PDF_COLORS.primary);

    const fullName = [values.firstName, values.middleName, values.lastName].filter(Boolean).join(' ').trim() || 'N/A';
    const personalInfoData = [
      ['Full Name', fullName],
      ['Business Phone', values.businessPhone || 'N/A'],
      ['Residence Address', values.residenceAddress || 'N/A'],
      ['City', values.residenceCity || 'N/A'],
      ['State', values.residenceState || 'N/A'],
      ['Zip Code', values.residenceZipCode || 'N/A'],
      ['Residence Phone', values.residencePhone || 'N/A'],
      ['Business Name', values.businessName || 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Field', 'Value']],
      body: personalInfoData,
      theme: 'striped',
      headStyles: {
        fillColor: PDF_COLORS.secondary,
        textColor: PDF_COLORS.white,
        fontStyle: 'bold',
        fontSize: PDF_FONTS.tableHeader,
      },
      bodyStyles: {
        fontSize: PDF_FONTS.tableBody,
        textColor: PDF_COLORS.dark,
      },
      alternateRowStyles: {
        fillColor: PDF_COLORS.secondaryLight,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 55 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== ASSETS SECTION ====================
  const result2 = addPageIfNeeded(doc, yPos);
  doc = result2.doc;
  yPos = result2.yPos;
  
  yPos = createSectionHeader(doc, 'Assets', yPos, PDF_COLORS.primary);

  const assetsData = [
    ['Cash on Hand & in Banks', formatCurrency(values.cashOnHandAndBanks)],
    ['Savings Accounts', formatCurrency(values.savingsAccounts)],
    ['IRA or Other Retirement', formatCurrency(values.iraOrOtherRetirement)],
    ['Accounts & Notes Receivable', formatCurrency(values.accountsAndNotesReceivable)],
    ['Life Insurance - Cash Surrender', formatCurrency(values.lifeInsuranceCashSurrender)],
    ['Stocks and Bonds', formatCurrency(values.stocksAndBonds)],
    ['Real Estate', formatCurrency(values.realEstate)],
    ['Automobile', formatCurrency(values.automobileTotalPresentValue)],
    ['Other Personal Property', formatCurrency(values.otherPersonalProperty)],
    ['Other Assets', formatCurrency(values.otherAssets)],
  ];

  const assetsDataWithTotal = [
    ...assetsData,
    ['TOTAL ASSETS', formatCurrency(totalAssets.toString())],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Asset Type', 'Amount']],
    body: assetsDataWithTotal,
    theme: 'striped',
    headStyles: {
      fillColor: PDF_COLORS.secondary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONTS.tableHeader,
    },
    bodyStyles: {
      fontSize: PDF_FONTS.tableBody,
      textColor: PDF_COLORS.dark,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.secondaryLight,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { halign: 'right', cellWidth: 'auto' },
    },
    margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    didParseCell: function(data: any) {
      if (data.row.index === assetsDataWithTotal.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 12;
        data.cell.styles.fillColor = PDF_COLORS.success;
        data.cell.styles.textColor = PDF_COLORS.white;
      }
    },
  });
  yPos = (doc as any).lastAutoTable.finalY + 15;

  // ==================== LIABILITIES SECTION ====================
  const result3 = addPageIfNeeded(doc, yPos);
  doc = result3.doc;
  yPos = result3.yPos;
  
  yPos = createSectionHeader(doc, 'Liabilities', yPos, PDF_COLORS.primary);

  const liabilitiesData = [
    ['Accounts Payable', formatCurrency(values.accountsPayable)],
    ['Notes Payable to Banks', formatCurrency(values.notesPayableToBanksAndOthers)],
    ['Installment Account (Auto)', formatCurrency(values.installmentAccountAuto)],
    ['Monthly Payments (Auto)', formatCurrency(values.moPaymentsAuto)],
    ['Installment Account (Other)', formatCurrency(values.installmentAccountOther)],
    ['Monthly Payments (Other)', formatCurrency(values.moPaymentsOther)],
    ['Loans on Life Insurance', formatCurrency(values.loansOnLifeInsurance)],
    ['Mortgages on Real Estate', formatCurrency(values.mortgagesOnRealEstate)],
    ['Unpaid Taxes', formatCurrency(values.unpaidTaxes)],
    ['Other Liabilities', formatCurrency(values.otherLiabilities)],
  ];

  const liabilitiesDataWithTotal = [
    ...liabilitiesData,
    ['TOTAL LIABILITIES', formatCurrency(totalLiabilities.toString())],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Liability Type', 'Amount']],
    body: liabilitiesDataWithTotal,
    theme: 'striped',
    headStyles: {
      fillColor: PDF_COLORS.secondary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONTS.tableHeader,
    },
    bodyStyles: {
      fontSize: PDF_FONTS.tableBody,
      textColor: PDF_COLORS.dark,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.secondaryLight,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { halign: 'right', cellWidth: 'auto' },
    },
    margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    didParseCell: function(data: any) {
      if (data.row.index === liabilitiesDataWithTotal.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 12;
        data.cell.styles.fillColor = PDF_COLORS.danger;
        data.cell.styles.textColor = PDF_COLORS.white;
      }
    },
  });
  yPos = (doc as any).lastAutoTable.finalY + 15;

  // ==================== CONTINGENT LIABILITIES SECTION ====================
  if (values.asEndorserOrCoMaker || values.legalClaimsAndJudgments || 
      values.provisionForFederalIncomeTax || values.otherSpecialDebt) {
    const result4 = addPageIfNeeded(doc, yPos);
    doc = result4.doc;
    yPos = result4.yPos;
    
    yPos = createSectionHeader(doc, 'Contingent Liabilities', yPos, PDF_COLORS.primary);

    const contingentData = [
      ['As Endorser or Co-Maker', values.asEndorserOrCoMaker || 'N/A'],
      ['Legal Claims & Judgments', values.legalClaimsAndJudgments || 'N/A'],
      ['Provision for Federal Income Tax', values.provisionForFederalIncomeTax || 'N/A'],
      ['Other Special Debt', values.otherSpecialDebt || 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Liability Type', 'Details']],
      body: contingentData,
      theme: 'striped',
    headStyles: {
      fillColor: PDF_COLORS.secondary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONTS.tableHeader,
    },
    bodyStyles: {
      fontSize: PDF_FONTS.tableBody,
      textColor: PDF_COLORS.dark,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.secondaryLight,
    },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== SOURCE OF INCOME SECTION ====================
  if (values.salary || values.netInvestmentIncome || values.realEstateIncome || values.otherIncome) {
    const result5 = addPageIfNeeded(doc, yPos);
    doc = result5.doc;
    yPos = result5.yPos;
    
    yPos = createSectionHeader(doc, 'Source of Income', yPos, PDF_COLORS.primary);

    const incomeData = [
      ['Salary', formatCurrency(values.salary)],
      ['Net Investment Income', formatCurrency(values.netInvestmentIncome)],
      ['Real Estate Income', formatCurrency(values.realEstateIncome)],
      ['Other Income', formatCurrency(values.otherIncome)],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Income Source', 'Amount']],
      body: incomeData,
      theme: 'striped',
    headStyles: {
      fillColor: PDF_COLORS.secondary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONTS.tableHeader,
    },
    bodyStyles: {
      fontSize: PDF_FONTS.tableBody,
      textColor: PDF_COLORS.dark,
    },
    alternateRowStyles: {
      fillColor: PDF_COLORS.secondaryLight,
    },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 70 },
        1: { halign: 'right', cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });

    if (values.descriptionOfOtherIncome) {
      yPos = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(PDF_FONTS.tableBody);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(PDF_COLORS.dark[0], PDF_COLORS.dark[1], PDF_COLORS.dark[2]);
      doc.text('Description of Other Income:', PDF_LAYOUT.margin, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
      const splitDescription = doc.splitTextToSize(values.descriptionOfOtherIncome, 180);
      doc.text(splitDescription, PDF_LAYOUT.margin, yPos);
    }
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== JOINT PERSON INFORMATION SECTION ====================
  if (values.jointPersonalInfo?.email || values.jointPersonalInfo?.ssn) {
    const result6 = addPageIfNeeded(doc, yPos);
    doc = result6.doc;
    yPos = result6.yPos;
    
    yPos = createSectionHeader(doc, 'Joint Person Information', yPos, PDF_COLORS.primary);

    const jointPersonalData = [
      ['Email', values.jointPersonalInfo?.email || 'N/A'],
      ['SSN', values.jointPersonalInfo?.ssn || 'N/A'],
      ['Date of Birth', values.jointPersonalInfo?.dateOfBirth || 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Field', 'Value']],
      body: jointPersonalData,
      theme: 'striped',
      headStyles: {
        fillColor: PDF_COLORS.secondary,
        textColor: PDF_COLORS.white,
        fontStyle: 'bold',
        fontSize: PDF_FONTS.tableHeader,
      },
      bodyStyles: {
        fontSize: PDF_FONTS.tableBody,
        textColor: PDF_COLORS.dark,
      },
      alternateRowStyles: {
        fillColor: PDF_COLORS.secondaryLight,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== JOINT PERSON INCOME SECTION ====================
  if (
    values.jointPersonalIncome?.annualIncome ||
    values.jointPersonalIncome?.employerFirstName ||
    values.jointPersonalIncome?.employerMiddleName ||
    values.jointPersonalIncome?.employerLastName
  ) {
    const result7 = addPageIfNeeded(doc, yPos);
    doc = result7.doc;
    yPos = result7.yPos;
    
    yPos = createSectionHeader(doc, 'Joint Person Income', yPos, PDF_COLORS.primary);

    const employerFullName = [
      values.jointPersonalIncome?.employerFirstName,
      values.jointPersonalIncome?.employerMiddleName,
      values.jointPersonalIncome?.employerLastName
    ].filter(Boolean).join(' ').trim() || 'N/A';
    
    const jointIncomeData = [
      ['Annual Income', formatCurrency(values.jointPersonalIncome?.annualIncome)],
      ['Employment Status', values.jointPersonalIncome?.employmentStatus || 'N/A'],
      ['Employer Name', employerFullName],
      ['Employer Phone', values.jointPersonalIncome?.employerPhone || 'N/A'],
      ['Job Title', values.jointPersonalIncome?.jobTitle || 'N/A'],
      ['Years Employed', values.jointPersonalIncome?.yearsEmployed || 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Field', 'Value']],
      body: jointIncomeData,
      theme: 'striped',
      headStyles: {
        fillColor: PDF_COLORS.secondary,
        textColor: PDF_COLORS.white,
        fontStyle: 'bold',
        fontSize: PDF_FONTS.tableHeader,
      },
      bodyStyles: {
        fontSize: PDF_FONTS.tableBody,
        textColor: PDF_COLORS.dark,
      },
      alternateRowStyles: {
        fillColor: PDF_COLORS.secondaryLight,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== JOINT ACCOUNT INFORMATION SECTION ====================
  if (values.jointAccount?.firstName || values.jointAccount?.middleName || values.jointAccount?.lastName || values.jointAccount?.address) {
    const result8 = addPageIfNeeded(doc, yPos);
    doc = result8.doc;
    yPos = result8.yPos;
    
    yPos = createSectionHeader(doc, 'Joint Account Information', yPos, PDF_COLORS.primary);

    const jointAccountFullName = [
      values.jointAccount?.firstName,
      values.jointAccount?.middleName,
      values.jointAccount?.lastName
    ].filter(Boolean).join(' ').trim() || 'N/A';

    const jointAccountData = [
      ['Account Name', jointAccountFullName],
      ['Phone', values.jointAccount?.phone || 'N/A'],
      ['Address', values.jointAccount?.address || 'N/A'],
      ['City', values.jointAccount?.city || 'N/A'],
      ['State', values.jointAccount?.state || 'N/A'],
      ['Zip Code', values.jointAccount?.zipCode || 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Field', 'Value']],
      body: jointAccountData,
      theme: 'striped',
      headStyles: {
        fillColor: PDF_COLORS.secondary,
        textColor: PDF_COLORS.white,
        fontStyle: 'bold',
        fontSize: PDF_FONTS.tableHeader,
      },
      bodyStyles: {
        fontSize: PDF_FONTS.tableBody,
        textColor: PDF_COLORS.dark,
      },
      alternateRowStyles: {
        fillColor: PDF_COLORS.secondaryLight,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ==================== DIGITAL SIGNATURE SECTION ====================
  if (values.digitalSignature) {
    const result9 = addPageIfNeeded(doc, yPos, 180);
    doc = result9.doc;
    yPos = result9.yPos;
    
yPos = createSectionHeader(doc, 'Digital Signature', yPos, PDF_COLORS.primary);
    
  doc.setDrawColor(PDF_COLORS.primary[0], PDF_COLORS.primary[1], PDF_COLORS.primary[2]);
  doc.setLineWidth(1);
  doc.rect(PDF_LAYOUT.margin, yPos, 100, 40);
    doc.addImage(values.digitalSignature, 'PNG', PDF_LAYOUT.margin + 2, yPos + 2, 96, 36);
    
    doc.setFontSize(PDF_FONTS.tableBody);
    doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
    doc.text(`Digitally signed on: ${new Date().toLocaleString()}`, PDF_LAYOUT.margin, yPos + 50);
  }

  // ==================== FOOTER ====================
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(PDF_FONTS.footer);
    doc.setTextColor(PDF_COLORS.secondary[0], PDF_COLORS.secondary[1], PDF_COLORS.secondary[2]);
    doc.text(
      `Page ${i} of ${pageCount} | Business Lending Hub - Financial Statement`,
      105,
      290,
      { align: 'center' }
    );
  }

  // Save the PDF
  const fullName = [values.firstName, values.middleName, values.lastName].filter(Boolean).join(' ').trim() || 'Customer';
  doc.save(
    `Financial_Statement_${fullName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  );
};

/**
 * Download financial info as PDF
 * @param values - The financial info values to generate PDF from
 */
export const downloadFinancialInfoPDF = (values: FinancialInfoValues): void => {
  const totalAssets = calculateTotalAssets(values);
  const totalLiabilities = calculateTotalLiabilities(values);
  generateFinancialInfoPDF(values, totalAssets, totalLiabilities);
};
